import { PageHeader } from 'components/PageHeader';
import { DocumentCategory, IncomingDocumentStatus, IncomingDocumentType } from 'src/types/enums';
import {
  approveIncomingDocument,
  getIncomingDocumentDebug,
  linkIncomingDocumentToOrder,
  linkIncomingDocumentToPurchase,
  predictIncomingInvoice,
  rejectIncomingDocument,
  splitIncomingDocument,
  updateIncomingDocumentType,
  useIncomingDocument,
  type IncomingDocument as IncomingDocumentModel,
} from '../models';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { invariant, nullthrows } from '@utils/invariant';
import classNames from '@utils/classnames';
import { Formik } from 'components/form/formik';
import { CheckIcon, WarningIcon } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import useSWR from 'swr';

import { Breadcrumb } from '../../../components/Breadcrumb';
import { Alert } from '../../../components/alert/Alert';
import { getDisplayError } from '../../../utils/get-display-error';
import { ConfirmDialog } from '../../../components/dialog/ConfirmDialog';
import { DataField } from '../../../components/DataField';
import {
  IncomingDocumentReviewReasonToText,
  IncomingDocumentStatusToText,
  IncomingDocumentTypeToText,
} from '../utils/status';
import { FormDialog } from '../../../components/dialog/FormDialog';
import { MinimalOrderComboboxField } from '../components/MinimalOrderComboboxField';
import { MinimalPurchaseComboboxField } from '../components/MinimalPurchaseComboboxField';
import { useMinimalGeneralSettings } from '../../../contexts/minimal-settings-context';
import { DocumentPreview, IDocumentPreviewProps } from '../../document/components/DocumentPreview';
import { rotateDocumentPages, verifyDocumentCategory } from '../../document/models';
import { SimpleSelectField } from '../../../components/select/SimpleSelectField';
import { DOCUMENT_CATEGORY_VALUES } from '../../document/constants.client';
import { Button } from '../../../components/button/Button';
import { formatDate, formatDateTime } from '../../../utils/date';
import { decimateAndFormatNumber } from '../../../utils/number';
import { DocumentSplitterDialog, IParsedPageRange } from '../components/DocumentSplitterDialog';
import { LinkButton } from '../../../components/button/ButtonLink';
import { PageHeading } from 'components/PageHeading';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../utils/track-event';
import { downloadTextFile } from '../../../utils/download';
import { DelayedSpinner } from 'components/Spinner';

export type IncomingDocument = IncomingDocumentModel;

const LINKABLE_INCOMING_TYPES: IncomingDocumentType[] = [
  IncomingDocumentType.Cmr,
  IncomingDocumentType.DeliveryNote,
  IncomingDocumentType.WeighSlip,
  IncomingDocumentType.PalletNote,
  IncomingDocumentType.AnnexII,
  IncomingDocumentType.T1Document,
  IncomingDocumentType.AnnexVII,
  IncomingDocumentType.Other,
];

const MANUAL_TYPE_OPTIONS = Object.values(IncomingDocumentType)
  .filter((t) => t !== IncomingDocumentType.TransportOrder)
  .map((t) => ({
    key: t,
    name: IncomingDocumentTypeToText[t],
  }));

const LinkOrderDialog: React.FC<{ incomingDoc: IncomingDocument; isBlocked: boolean }> = (props) => {
  const { incomingDoc, isBlocked } = props;
  const navigate = useNavigate();

  const { refreshIncomingDocCount } = useMinimalGeneralSettings();

  return (
    <FormDialog
      triggerText="Link Order"
      triggerColor={incomingDoc.status !== IncomingDocumentStatus.Approved ? 'primary' : 'default'}
      title={`Link ${IncomingDocumentTypeToText[incomingDoc.type]} document aan order`}
      submitText="Link Order"
      initialValues={{ order: incomingDoc.order }}
      isDisabled={isBlocked}
      triggerTitle={
        isBlocked
          ? 'Kan dit document niet linken, bevat paginas die niet in een order mogen.'
          : 'Link document aan een order'
      }
      onSubmit={async (values) => {
        try {
          const order = nullthrows(values.order, 'Er is geen order geselecteerd');
          await linkIncomingDocumentToOrder(incomingDoc.id, order.id);
          trackEvent('incoming_document_linked_to_order', {
            incoming_doc_id: incomingDoc.id,
            order_id: order.id,
            document_type: incomingDoc.type,
          });
          refreshIncomingDocCount();
          toast.success('Order gelinkt');
          navigate('..');
        } catch (err) {
          captureException(err);
          toast.error('Kon order niet linken: ' + getDisplayError(err));
        }
      }}
    >
      <MinimalOrderComboboxField name="order" />
    </FormDialog>
  );
};

const LinkPurchaseDialog: React.FC<{ incomingDoc: IncomingDocument }> = (props) => {
  const { incomingDoc } = props;
  const navigate = useNavigate();

  const { refreshIncomingDocCount } = useMinimalGeneralSettings();

  return (
    <FormDialog
      triggerText="Link aan AKF"
      triggerColor={incomingDoc.status !== IncomingDocumentStatus.Approved ? 'primary' : 'default'}
      title="Link document aan aankoopfactuur"
      submitText="Link aan AKF"
      initialValues={{ purchase: null as { id: string } | null }}
      onSubmit={async (values) => {
        try {
          const purchase = nullthrows(values.purchase, 'Er is geen aankoopfactuur geselecteerd');
          await linkIncomingDocumentToPurchase(incomingDoc.id, purchase.id);
          trackEvent('incoming_document_linked_to_purchase', {
            incoming_doc_id: incomingDoc.id,
            purchase_id: purchase.id,
          });
          refreshIncomingDocCount();
          toast.success('Document gelinkt aan aankoopfactuur');
          navigate('..');
        } catch (err) {
          captureException(err);
          toast.error('Kon document niet linken: ' + getDisplayError(err));
        }
      }}
    >
      <MinimalPurchaseComboboxField name="purchase" />
    </FormDialog>
  );
};

const IncomingDocumentDebugInformation: React.FC<{ incomingDocId: string }> = ({ incomingDocId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const {
    data: debugInfo,
    error,
    isLoading,
  } = useSWR(
    isOpen ? ['incoming-document-debug', incomingDocId] : null,
    () => getIncomingDocumentDebug(incomingDocId),
    {
      revalidateOnFocus: false,
    },
  );

  const downloadDebugMarkdown = async () => {
    setIsDownloading(true);
    try {
      const debug = debugInfo ?? (await getIncomingDocumentDebug(incomingDocId));
      downloadTextFile(debug.markdown, debug.filename, 'text/markdown;charset=utf-8');
    } catch (err) {
      captureException(err);
      toast.error(`Kon debug informatie niet downloaden: ${getDisplayError(err)}`);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="mt-8 border-t border-border pt-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="heading-two mb-1">Debug informatie</div>
          <p className="text-sm text-fg-secondary">
            Exporteer OCR-tekst, LLM output, validatiecontext, order/purchase gegevens en audit logs als markdown.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            color="secondary"
            onTrigger={() => {
              setIsOpen((prev) => !prev);
            }}
          >
            {isOpen ? 'Verberg debug informatie' : 'Bekijk debug informatie'}
          </Button>
          <Button color="primary" onTrigger={downloadDebugMarkdown} isLoading={isDownloading}>
            Download markdown
          </Button>
        </div>
      </div>

      {isOpen && (
        <div className="mt-4">
          {error ? (
            <Alert type="error">Kon debug informatie niet laden: {getDisplayError(error)}</Alert>
          ) : isLoading || !debugInfo ? (
            <div className="rounded border border-border bg-surface-sunken p-4 text-sm text-fg-secondary">
              Debug informatie laden...
            </div>
          ) : (
            <pre className="max-h-144 overflow-auto whitespace-pre-wrap rounded border border-border bg-surface-sunken p-4 font-mono text-xs text-fg">
              {debugInfo.markdown}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

const IncomingDocPage = () => {
  const { incomingDocId } = useParams<{ incomingDocId: string }>();
  invariant(incomingDocId);
  const { isLoading } = useIncomingDocument(incomingDocId);

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  return <IncomingDocDetail incomingDocId={incomingDocId} />;
};

const IncomingDocDetail: React.FC<{ incomingDocId: string }> = ({ incomingDocId }) => {
  const navigate = useNavigate();
  // Cache-shared with the parent's useIncomingDocument call above, so data is
  // already resolved by the time this component mounts.
  const { data, mutate: refreshIncomingDoc } = useIncomingDocument(incomingDocId);
  const [pageRotations, setPageRotations] = useState<NonNullable<IDocumentPreviewProps['rotations']>>([]);
  const [previewKey, setPreviewKey] = useState(0);

  const incomingDoc = nullthrows(data, 'Incoming document not found');

  const { refreshIncomingDocCount } = useMinimalGeneralSettings();
  const [isRotatingPages, setIsRotatingPages] = useState(false);

  const originalType = incomingDoc.type;
  const newType = useMemo(() => {
    const filtered = incomingDoc.pages.filter((v) => v.prediction.confidence > 0.8).map((p) => p.prediction);

    const containsInvoice = filtered.find(
      (p) => p.category === DocumentCategory.Invoice || p.category === DocumentCategory.CreditNote,
    );
    if (containsInvoice) {
      return IncomingDocumentType.Invoice;
    }

    const containsCmr = filtered.find(
      (p) => p.category === DocumentCategory.Cmr || p.category === DocumentCategory.DeliveryNote,
    );
    if (containsCmr) {
      return IncomingDocumentType.Cmr;
    }

    return IncomingDocumentType.Other;
  }, [incomingDoc.pages]);

  const linkingBlocked = !!incomingDoc.pages.find((p) => {
    return (
      p.prediction.category === DocumentCategory.Invoice || p.prediction.category === DocumentCategory.TransportOrder
    );
  });

  const executeDocumentSplit = async (pageRanges: IParsedPageRange[]) => {
    await splitIncomingDocument(
      incomingDoc.id,
      pageRanges.map((v) => {
        return {
          startPage: v.start,
          endPage: v.end,
        };
      }),
    );
    navigate('..');
  };

  const persistDocumentRotations = async () => {
    setIsRotatingPages(true);
    try {
      const rotations = pageRotations.filter((v) => v.rotation !== 0);
      await rotateDocumentPages(
        incomingDoc.document.id,
        rotations.map((v) => {
          return v.pageNumber;
        }),
        rotations[0].rotation,
      );
      setPreviewKey((prev) => prev + 1);
      setPageRotations([]);
      toast.success('Document rotaties opgeslagen');
    } catch (err) {
      captureException(err);
      toast.error(`Kon document rotaties niet opslaan: ${getDisplayError(err)}`);
    } finally {
      setIsRotatingPages(false);
    }
  };

  const order = incomingDoc.order;
  const purchaseInvoice = incomingDoc.purchaseInvoice;
  return (
    <>
      <PageHeader title={`Inkomend Document - ${incomingDoc.document.name}`} />

      <PageHeading
        leftSide={
          <Breadcrumb
            parentItem={{
              name: 'Inkomende Documenten',
              to: '/internal/incoming-docs',
            }}
            currentItem={incomingDoc.document.name}
          />
        }
      />

      <div className="px-4">
        {incomingDoc.status === IncomingDocumentStatus.New && incomingDoc.reviewReasons.length > 0 && (
          <Alert
            type="warning"
            className="my-4"
            icon={<WarningIcon weight="fill" />}
            title="Dit document moet handmatig nagekeken worden"
          >
            <div>
              De automatische verwerking heeft dit document doorgestuurd voor manuele review om de volgende reden(en):
            </div>
            <ul className="mt-2 list-disc pl-5 space-y-0.5">
              {incomingDoc.reviewReasons.map((reason) => (
                <li key={reason}>{IncomingDocumentReviewReasonToText[reason]}</li>
              ))}
            </ul>
          </Alert>
        )}

        <div className="flex justify-between gap-4 mb-4">
          <div className="flex gap-8">
            <DataField title="Status">{IncomingDocumentStatusToText[incomingDoc.status]}</DataField>
            <DataField title="Document type">{IncomingDocumentTypeToText[incomingDoc.type]}</DataField>
            <DataField title="Datum">{formatDateTime(incomingDoc.date)}</DataField>
            {order ? (
              <>
                <DataField title="Order">{order.orderNumber}</DataField>
                <DataField title="Klant">{`${order.customer?.name ?? ''} ${order.customerRef}`}</DataField>
                {!!order.supplier && <DataField title="Vervoerder">{order.supplier.name}</DataField>}
              </>
            ) : null}
            {purchaseInvoice ? (
              <>
                <DataField title="Aankoop">{purchaseInvoice.invoiceNumber || purchaseInvoice.id}</DataField>
                <DataField title="Vervoerder">{purchaseInvoice.supplier.name}</DataField>
              </>
            ) : null}
          </div>
          <div className="flex justify-end gap-4">
            {purchaseInvoice && (
              <LinkButton to={`/internal/finance/purchases/${purchaseInvoice.id}`} color="primary">
                Aankoopfactuur
              </LinkButton>
            )}

            {incomingDoc.status === IncomingDocumentStatus.New && (
              <>
                {LINKABLE_INCOMING_TYPES.includes(incomingDoc.type) && (
                  <LinkOrderDialog incomingDoc={incomingDoc} isBlocked={linkingBlocked} />
                )}

                <FormDialog
                  triggerText="Wijzig documenttype"
                  triggerColor="secondary"
                  title="Documenttype aanpassen"
                  submitText="Opslaan"
                  initialValues={{
                    type: MANUAL_TYPE_OPTIONS.find((o) => o.key === incomingDoc.type) ?? MANUAL_TYPE_OPTIONS[0],
                  }}
                  onSubmit={async (values) => {
                    try {
                      const newType = nullthrows(
                        values.type?.key,
                        'Selecteer een documenttype',
                      ) as IncomingDocumentType;
                      await updateIncomingDocumentType(incomingDoc.id, newType);
                      refreshIncomingDoc();
                      refreshIncomingDocCount();
                      toast.success('Documenttype bijgewerkt');
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon documenttype niet bijwerken: ${getDisplayError(err)}`);
                    }
                  }}
                >
                  <SimpleSelectField labelText="Type" name="type" items={MANUAL_TYPE_OPTIONS} />
                </FormDialog>

                {!!pageRotations.length && (
                  <Button
                    color="primary"
                    onTrigger={() => {
                      persistDocumentRotations();
                    }}
                    isLoading={isRotatingPages}
                  >
                    Pas document rotaties aan
                  </Button>
                )}

                {!purchaseInvoice && incomingDoc.type === IncomingDocumentType.Invoice && (
                  <LinkPurchaseDialog incomingDoc={incomingDoc} />
                )}

                {!purchaseInvoice && incomingDoc.type === IncomingDocumentType.Invoice && (
                  <ConfirmDialog
                    triggerColor="primary"
                    triggerText="Factuur aanmaken"
                    title="Probeer aankoopfactuur opnieuw aan te maken?"
                    submitText="Aanmaken"
                    description="Ben je zeker dat je aankoopfactuur opnieuw wilt aanmaken?"
                    onSubmit={async () => {
                      try {
                        await predictIncomingInvoice(incomingDoc.id);
                        refreshIncomingDoc();
                        refreshIncomingDocCount();
                        toast.success('Factuur aangemaakt');
                      } catch (err) {
                        captureException(err);
                        toast.error(`Kon factuur niet aanmaken: ${getDisplayError(err)}`);
                      }
                    }}
                  />
                )}

                {incomingDoc.type === IncomingDocumentType.Invoice && (
                  <ConfirmDialog
                    triggerColor="primary"
                    triggerText="Goedkeuren"
                    title="Ben je zeker dat je dit document wilt goedkeuren?"
                    submitText="Goedkeuren"
                    description="Ben je zeker dat je dit document wilt goedkeuren?"
                    onSubmit={async () => {
                      try {
                        await approveIncomingDocument(incomingDoc.id);
                        refreshIncomingDocCount();
                        toast.success('Document goedgekeurd');
                        navigate('..');
                      } catch (err) {
                        captureException(err);
                        toast.error(`Kon document niet goedkeuren: ${getDisplayError(err)}`);
                      }
                    }}
                  />
                )}

                {originalType !== newType && (
                  <ConfirmDialog
                    triggerColor="primary"
                    triggerText="Werk document type bij"
                    title={`Ben je zeker dat je het document type wilt veranderen naar ${IncomingDocumentTypeToText[newType]}?`}
                    submitText="Pas type aan"
                    description="Ben je zeker dat je het document type wilt veranderen? Dit zal ervoor zorgen dat zaken zoals automatische herkenning gedraaid worden op het nieuwe type."
                    onSubmit={async () => {
                      try {
                        await updateIncomingDocumentType(incomingDoc.id, newType);
                        refreshIncomingDoc();
                        refreshIncomingDocCount();
                        toast.success('Document bijgewerkt');
                      } catch (err) {
                        captureException(err);
                        toast.error(`Kon document niet bijwerken: ${getDisplayError(err)}`);
                      }
                    }}
                  />
                )}

                {incomingDoc.pages.length > 1 && (
                  <DocumentSplitterDialog pages={incomingDoc.pages} executeSplit={executeDocumentSplit} />
                )}

                {
                  <ConfirmDialog
                    triggerColor="secondary"
                    triggerText="Weiger document"
                    title="Ben je zeker dat je dit document wilt weigeren?"
                    submitText="Weiger document"
                    description="Ben je zeker dat je dit document wilt weigeren?"
                    onSubmit={async () => {
                      try {
                        await rejectIncomingDocument(incomingDoc.id);
                        refreshIncomingDocCount();
                        toast.success('Document geweigerd');
                        navigate('..');
                      } catch (err) {
                        captureException(err);
                        toast.error(`Kon document niet weigeren: ${getDisplayError(err)}`);
                      }
                    }}
                  />
                }
              </>
            )}
          </div>
        </div>

        {!purchaseInvoice && incomingDoc.type === IncomingDocumentType.Invoice && (
          <Alert type="error" className="my-4">
            Automatisch aanmaken van aankoopfactuur mislukt.
          </Alert>
        )}

        {purchaseInvoice && incomingDoc.invoiceTotalMismatch && (
          <Alert type="error" className="my-4">
            Totaal bedrag op document en verwacht totaal bedrag komen niet overeen.
          </Alert>
        )}

        <div className="flex gap-8">
          <div style={{ width: 802 }}>
            <DocumentPreview
              key={previewKey}
              document={incomingDoc.document}
              rotations={pageRotations}
              onRotate={(pageNumber, newRotation) => {
                setPageRotations((prev) => {
                  if (newRotation === 0) {
                    return prev.filter((v) => v.pageNumber !== pageNumber);
                  }

                  const existing = prev.find((v) => v.pageNumber === pageNumber);
                  if (!existing) {
                    return [...prev, { pageNumber, rotation: newRotation }];
                  } else {
                    existing.rotation = newRotation;
                    return [...prev];
                  }
                });
              }}
            />
          </div>
          <div className="w-full">
            <div className="mb-8 w-full max-w-96">
              <div className="heading-two mb-2">AI Predictions</div>
              {incomingDoc.aiAnalysis &&
                (() => {
                  const ai = incomingDoc.aiAnalysis;
                  const roundedConfidence = Math.round(ai.confidence * 100);
                  return (
                    <div className="mb-4 flex flex-col gap-1 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Type:</span>
                        <span>{ai.type}</span>
                        <span
                          className={classNames('font-medium ml-auto', {
                            'text-positive-text': roundedConfidence > 95,
                            'text-negative-text': roundedConfidence < 90,
                          })}
                        >
                          {roundedConfidence}%
                        </span>
                      </div>
                      <div className="text-fg-muted text-xs">
                        Pagina&apos;s {ai.pageStart}–{ai.pageEnd}
                        {ai.signaturePresent === true && ' • Handtekening: ja'}
                        {ai.signaturePresent === false && ' • Handtekening: nee'}
                      </div>
                      {ai.detectedCustomerName && (
                        <div className="text-xs text-fg-muted">Klant op document: {ai.detectedCustomerName}</div>
                      )}
                      {ai.detectedSupplierName && (
                        <div className="text-xs text-fg-muted">Leverancier op document: {ai.detectedSupplierName}</div>
                      )}
                      {ai.detectedCarrierName && (
                        <div className="text-xs text-fg-muted">Vervoerder op document: {ai.detectedCarrierName}</div>
                      )}
                      {ai.uncertaintyFlags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {ai.uncertaintyFlags.map((flag) => (
                            <span
                              key={flag}
                              className="text-xs px-1.5 py-0.5 rounded bg-brand-subtle text-brand-text border border-brand-border"
                            >
                              {flag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              <div className="w-full">
                {incomingDoc.pages
                  .sort((a, b) => a.pageIndex - b.pageIndex)
                  .map((p) => {
                    const roundedConfidence = Math.round(p.prediction.confidence * 100);
                    return (
                      <Formik
                        initialValues={{
                          category: DOCUMENT_CATEGORY_VALUES.find((v) => v.key === p.prediction.category),
                        }}
                        onSubmit={async (newValues) => {
                          try {
                            if (!newValues.category) {
                              throw new Error('Geen categorie geselecteerd');
                            }

                            await verifyDocumentCategory(p.prediction.id, newValues.category.key as DocumentCategory);
                            toast.success('Categorie geverifieerd');
                          } catch (err) {
                            captureException(err);
                            toast.error(`Kon categorie niet verifiëren: ${getDisplayError(err)}`);
                          }
                        }}
                        key={p.id}
                      >
                        {({ handleSubmit, isSubmitting }) => {
                          return (
                            <form className="w-full flex items-center gap-4" onSubmit={handleSubmit}>
                              <div className="whitespace-nowrap">{`Pagina ${p.pageIndex + 1}`}</div>
                              <SimpleSelectField
                                labelText="Categorie"
                                items={DOCUMENT_CATEGORY_VALUES}
                                name="category"
                              />
                              <div
                                className={classNames('font-medium', {
                                  'text-positive-text': roundedConfidence > 95,
                                  'text-negative-text': roundedConfidence < 90,
                                })}
                              >{`${roundedConfidence}%`}</div>
                              <div>
                                <Button
                                  type="submit"
                                  color={p.prediction.isVerified ? 'default' : 'primary'}
                                  isLoading={isSubmitting}
                                >
                                  <CheckIcon className="w-4 h-4" />
                                </Button>
                              </div>
                            </form>
                          );
                        }}
                      </Formik>
                    );
                  })}
              </div>

              {incomingDoc.invoicePrediction && (
                <div>
                  <DataField title="Datum">
                    {incomingDoc.invoicePrediction.date ? formatDate(incomingDoc.invoicePrediction.date) : '-'}
                  </DataField>
                  <DataField title="Factuur nummer">{incomingDoc.invoicePrediction.invoiceNr ?? '-'}</DataField>
                  <DataField title="Klant naam">{incomingDoc.invoicePrediction.customerName ?? '-'}</DataField>
                  <DataField title="BTW Nummer Klant">{incomingDoc.invoicePrediction.customerTaxId ?? '-'}</DataField>
                  <DataField title="Leverancier naam">{incomingDoc.invoicePrediction.supplierName ?? '-'}</DataField>
                  <DataField title="BTW Nummer Leverancier">
                    {incomingDoc.invoicePrediction.supplierTaxId ?? '-'}
                  </DataField>
                  <DataField title="Totaal bedrag">
                    {incomingDoc.invoicePrediction.totalAmount
                      ? `€ ${decimateAndFormatNumber(incomingDoc.invoicePrediction.totalAmount, 2, {
                          decimalSeperator: ',',
                        })}`
                      : '-'}
                  </DataField>
                  <DataField title="Transport Orders">
                    {incomingDoc.invoicePrediction.transportOrderRefs.join(', ')}
                  </DataField>
                  <DataField title="Confidence">{`${Math.round(
                    incomingDoc.invoicePrediction.confidence * 100,
                  )}%`}</DataField>
                </div>
              )}
            </div>

            <div className="heading-two mb-2">Email Inhoud</div>
            <div>Van: {incomingDoc.from}</div>
            <div>Onderwerp: {incomingDoc.subject}</div>
            <div className="whitespace-pre-line">{incomingDoc.content}</div>
          </div>
        </div>

        <IncomingDocumentDebugInformation incomingDocId={incomingDoc.id} />
      </div>
    </>
  );
};

export default IncomingDocPage;
